import {
  Box,
  Button,
  Flex,
  Input,
  InputDescription,
  InputLabel,
  Text,
} from '@huspy/briks-web';
import { UseFormReturnType } from '@mantine/form';
import { EmailRecipientsInput } from '@modules/opportunities/presentation/pages/CaseCreation/components/EmailRecipientsInput';
import { useTranslation } from 'react-i18next';
import { Image } from '@mantine/core';
import { imagesPath } from '@shared/css.const';
import { VStack } from '@huspy/styled-system/jsx';
import SingleDocumentCard from '@modules/opportunities/presentation/pages/CaseCreation/components/SingleDocumentCard';
import useDownloadDocument from '@modules/opportunities/entities/document/mutation/useDownloadDocument';
import { CaseCreationSubmitApplicationsRoute } from '@modules/opportunities/presentation/routes';
import ScrollArea from '@components/v2/ScrollArea';
import { RichText } from '@components/v2/RichText';
import {
  ComposeEmailFormValues,
  EMAIL_CHANNEL_PROVIDER,
  EmailChannelProviderType,
} from './const';

// eslint-disable-next-line max-len
const baseTranslation = 'opportunity.caseSubmission.submitApplications.bankAndHuspySubmission.directToBankSlido.composeEmailForm';

const SubmissionMethodBody = ({
  title,
  icon,
  goBack,
}: {
  title: string;
  icon: string;
  goBack: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Flex
      p='4'
      bg='indigo.50'
      justify='space-between'
      align='center'
      gap='2'
      borderRadius='3'
    >
      <Flex align='center'>
        <Box w='48' h='48'>
          <Image src={ `${imagesPath}/${icon}` } w='100%' h='100%' />
        </Box>
        <Text textAlign='center' size='lg' fontWeight='semibold' ml='3'>
          {title}
        </Text>
      </Flex>
      <Button type='button' size='sm' variant='ghost' onClick={ goBack }>
        {t(`${baseTranslation}.changeSubmissionButton`)}
      </Button>
    </Flex>
  );
};
const ChangeSubmissionMethod = ({
  emailChannelProvider,
  goBack,
}: {
  emailChannelProvider: EmailChannelProviderType;
  goBack: () => void;
}) => {
  const { t } = useTranslation();

  if (emailChannelProvider === EMAIL_CHANNEL_PROVIDER.SELF) {
    return (
      <SubmissionMethodBody
        title={ t(`${baseTranslation}.sendToYourself`) }
        icon='mail_box_2.png'
        goBack={ goBack }
      />
    );
  }
  return (
    <SubmissionMethodBody
      title={ t(`${baseTranslation}.sendByYourOwn`) }
      icon='mail_box_1.png'
      goBack={ goBack }
    />
  );
};

const downloadFile = (url: string) => {
  window?.open(url, '_blank')?.focus();
};

export const ComposeEmail = ({
  form,
  emailChannelProvider,
  goBack,
  attachedFiles,
}: {
  form: UseFormReturnType<ComposeEmailFormValues>;
  emailChannelProvider: EmailChannelProviderType;
  attachedFiles: (
    | {
      type: 'personal';
      applicantId: string;
      name: string;
      id: string;
    }
    | {
      type: 'bank';
      name: string;
      id: string;
    }
  )[];
  goBack: () => void;
}) => {
  const { t } = useTranslation();
  const { opportunityId } = CaseCreationSubmitApplicationsRoute.useParams();
  const { mutateAsync: downloadDocument, isPending: isPendingDownload } = useDownloadDocument(opportunityId);

  return (
    <Flex direction='column' gap='6' w='100%' p='8'>
      <ChangeSubmissionMethod
        emailChannelProvider={ emailChannelProvider }
        goBack={ goBack }
      />
      <Box mb='2'>
        <Text size='2xl' mb='1' fontWeight='semibold'>
          {t(`${baseTranslation}.title`)}
        </Text>
        <Text size='sm' color='neutral.500'>
          {t(`${baseTranslation}.subtitle`)}
        </Text>
      </Box>
      <EmailRecipientsInput
        name='sentTo'
        label={ t(`${baseTranslation}.fields.sendTo`) }
        onValueChange={ (emails: string[]) =>
          form.setFieldValue('sendTo', emails) }
        fromErrors={ form.errors?.sendTo?.toString() }
        defaultEmail={ form.getInputProps('sendTo').value?.[0] }
        disabled={ emailChannelProvider === EMAIL_CHANNEL_PROVIDER.SELF }
      />
      {emailChannelProvider === EMAIL_CHANNEL_PROVIDER.THIRD_PARTY && (
        <EmailRecipientsInput
          name='cc'
          label={ t(`${baseTranslation}.fields.cc`) }
          onValueChange={ (emails: string[]) => form.setFieldValue('cc', emails) }
          fromErrors={ form.errors?.cc?.toString() }
        />
      )}
      <Box w='100%'>
        <InputLabel isError={ !!form.getInputProps('subject')?.error }>
          {t(`${baseTranslation}.fields.subject`)}
        </InputLabel>
        <Input
          placeholder={ t(`${baseTranslation}.fields.subject`) }
          type='text'
          name='subject'
          { ...form.getInputProps('subject') }
        />
        <InputDescription isError={ !!form.getInputProps('subject')?.error }>
          {form.getInputProps('subject')?.error ?? ''}
        </InputDescription>
      </Box>
      <Box w='100%'>
        <InputLabel isError={ !!form.getInputProps('body')?.error }>
          {t(`${baseTranslation}.fields.body`)}
        </InputLabel>
        <RichText { ...form.getInputProps('body') } />
        <InputDescription isError={ !!form.getInputProps('body')?.error }>
          {form.getInputProps('body')?.error ?? ''}
        </InputDescription>
        {emailChannelProvider === EMAIL_CHANNEL_PROVIDER.THIRD_PARTY && (
          <Text size='xs' mt='2' color='neutral.500' fontStyle='italic'>
            {t(`${baseTranslation}.disclaimer`)}
          </Text>
        )}
      </Box>
      <VStack w='100%' gap='7' mt='8'>
        <Box w='100%'>
          <Text size='2xl' weight='medium'>
            {t(`${baseTranslation}.attachments.title`)}
          </Text>
          <Text size='sm' color='text-tertiary'>
            {t(`${baseTranslation}.attachments.subtitle`)}
          </Text>
        </Box>
        <Flex w='100%' gap='spacing-3' direction='column'>
          <Text size='sm' color='text-secondary' weight='semiBold'>
            {t(`${baseTranslation}.attachments.files`)}
          </Text>
          <Flex gap='spacing-2' direction='column'>
            <ScrollArea h='200px'>
              {attachedFiles.map((attachment) => (
                <SingleDocumentCard
                  key={ attachment.id }
                  fileName={ attachment.name }
                  { ...(attachment.type === 'personal' && {
                    onDownload: async () => {
                      const response = await downloadDocument({
                        opportunityApplicantDocumentExternalID: attachment.id,
                        opportunityApplicantExternalID: attachment.applicantId,
                      });
                      downloadFile(response.url);
                    },
                    isPendingDownload,
                  }) }
                />
              ))}
            </ScrollArea>
          </Flex>
        </Flex>
      </VStack>
    </Flex>
  );
};
